import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CarteTerritoire,
  centreEmprise,
  emprise,
  FOND_OPENFREEMAP,
  type MarqueurCarte,
} from '@eec/ui';
import { lireLimites } from './CarteEquipements';
import { messageErreur } from '../../api/erreurs';
interface Ligne {
  province_code: string;
  province: string;
  incidents: number;
  incidents_ouverts: number;
  incidents_critiques: number;
  signalements: number;
  equipements: number;
}
export function CartePilotage({ lignes, tronque }: { lignes: Ligne[]; tronque: boolean }) {
  const [zoom, setZoom] = useState('gabon');
  const [fond, setFond] = useState(true);
  const limites = useQuery({
    queryKey: ['limites-cartographiques-gabon'],
    queryFn: ({ signal }) => lireLimites(signal),
    staleTime: Infinity,
  });
  const groupes = new Map<string, Ligne>();
  for (const l of lignes) {
    if (typeof l.province_code !== 'string' || !l.province_code) continue;
    const g = groupes.get(l.province_code) ?? {
      province_code: l.province_code,
      province: l.province,
      incidents: 0,
      incidents_ouverts: 0,
      incidents_critiques: 0,
      signalements: 0,
      equipements: 0,
    };
    for (const k of [
      'incidents',
      'incidents_ouverts',
      'incidents_critiques',
      'signalements',
      'equipements',
    ] as const)
      g[k] += l[k];
    groupes.set(l.province_code, g);
  }
  const provinces = [...groupes.values()].sort((a, b) =>
    a.province_code.localeCompare(b.province_code),
  );
  const echelles = limites.data?.filter((t) => t.id === 'gabon' || groupes.has(t.id)) ?? [];
  const marqueurs: MarqueurCarte[] = provinces.flatMap((p, i) => {
    const territoire = echelles.find((t) => t.id === p.province_code);
    if (!territoire?.geometrie) return [];
    const [longitude, latitude] = centreEmprise(emprise(territoire.geometrie));
    return [
      {
        id: p.province_code,
        numero: i + 1,
        longitude,
        latitude,
        libelle: `${p.province} : ${p.incidents_ouverts} incident(s) ouvert(s), dont ${p.incidents_critiques} critique(s)`,
        ton: p.incidents_critiques > 0 ? 'retard' : p.incidents_ouverts > 0 ? 'ouvert' : 'autre',
      },
    ];
  });
  const courante = echelles.some((t) => t.id === zoom) ? zoom : 'gabon';
  const surfaces = {
    type: 'FeatureCollection' as const,
    features: echelles
      .filter(
        (t) =>
          t.niveau === 'province' && t.geometrie && (courante === 'gabon' || t.id === courante),
      )
      .map((t) => ({
        type: 'Feature' as const,
        properties: { libelle: t.libelle },
        geometry: t.geometrie!,
      })),
  };
  return (
    <section aria-label="Carte nationale du pilotage" className="eec-carte">
      <h2>Les réseaux par province</h2>
      <p>
        Totaux des organismes présents dans le tableau, avec les mêmes filtres. Les repères
        représentent les provinces, sans localiser les incidents. Une province absente n’a pas de
        données dans la sélection ; cela ne prouve pas l’absence de panne.
      </p>
      {tronque ? (
        <p role="alert">
          Affinez les filtres : la carte est masquée pour éviter des totaux incomplets.
        </p>
      ) : (
        <>
          {lignes.some((l) => !l.province_code) && (
            <p role="alert">
              Des données sans code de province ne peuvent pas être cartographiées. Consultez le
              tableau par organisme.
            </p>
          )}
          <label>
            <input type="checkbox" checked={fond} onChange={(e) => setFond(e.target.checked)} />
            Afficher le fond de rues externe
          </label>
          {limites.error && (
            <p role="alert">
              {messageErreur(limites.error)} Les totaux restent disponibles ci-dessous.
            </p>
          )}
          {limites.isPending && <p>Chargement des limites provinciales…</p>}
          {echelles.length > 0 && (
            <CarteTerritoire
              titre="Pilotage agrégé des réseaux"
              echelles={echelles}
              echelle={courante}
              onEchelle={setZoom}
              marqueurs={marqueurs}
              marqueurActif={courante === 'gabon' ? null : courante}
              onMarqueur={setZoom}
              fond={fond ? FOND_OPENFREEMAP : null}
              hauteur={420}
              surfaces={surfaces}
              libellesTon={{
                retard: 'Avec incident critique ouvert',
                ouvert: 'Avec incident ouvert',
                autre: 'Sans incident ouvert enregistré',
              }}
              libelleObjets={{ singulier: 'province renseignée', pluriel: 'provinces renseignées' }}
            />
          )}
          {limites.data && marqueurs.length !== provinces.length && (
            <p role="alert">
              Certaines provinces ne disposent pas de limite cartographique correspondante. Leurs
              totaux restent dans le tableau.
            </p>
          )}
          {provinces.length === 0 ? (
            <p>Aucune donnée réseau à représenter dans cette sélection.</p>
          ) : (
            <div
              role="region"
              aria-label="Totaux provinciaux"
              tabIndex={0}
              style={{ overflowX: 'auto' }}
            >
              <table>
                <caption>Totaux provinciaux de la sélection</caption>
                <thead>
                  <tr>
                    {[
                      'Province',
                      'Incidents enregistrés',
                      'Incidents ouverts',
                      'Critiques ouverts',
                      'Signalements',
                      'Équipements',
                    ].map((t) => (
                      <th scope="col" key={t}>
                        {t}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {provinces.map((p) => (
                    <tr key={p.province_code}>
                      <th scope="row">
                        <button
                          onClick={() => setZoom(p.province_code)}
                          disabled={!echelles.some((t) => t.id === p.province_code)}
                          aria-pressed={courante === p.province_code}
                        >
                          {p.province}
                        </button>
                      </th>
                      <td>{p.incidents}</td>
                      <td>{p.incidents_ouverts}</td>
                      <td>{p.incidents_critiques}</td>
                      <td>{p.signalements}</td>
                      <td>{p.equipements}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <small>
            Repères au centre des emprises géographiques ; limites OpenStreetMap sans valeur de
            bornage officiel. Le zoom ne change pas les filtres du tableau.
          </small>
        </>
      )}
    </section>
  );
}
